import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import { normalizeChoices, reorderChoices } from '../utils/helpers';
import { useFormState } from '../contexts/FormStateContext';
import styles from './fields.module.css';

export interface RadioGroupFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

export const RadioGroupField: React.FC<RadioGroupFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const [showOther, setShowOther] = useState(false);
  const formState = useFormState();
  const error = errors[element.name];
  const validationError = formState.validationErrors[element.name];

  const normalizedChoices = normalizeChoices(element.choices);
  const choices = reorderChoices(normalizedChoices, element.choicesOrder);
  const hasOtherOption = element.showOtherItem;

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={element.defaultValue || ''}
        render={({ field }) => (
          <div className={styles.radioGroup}>
            {choices.map((choice, index) => (
              <label key={index} className={styles.radioOption}>
                <input
                  type="radio"
                  name={element.name}
                  value={choice.value}
                  checked={field.value === choice.value}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setShowOther(false);
                    formState.markFieldAsTouched(element.name);
                    formState.clearValidationError(element.name);
                  }}
                  disabled={element.readOnly}
                />
                <span>{choice.text}</span>
              </label>
            ))}
            {hasOtherOption && (
              <>
                <label className={styles.radioOption}>
                  <input
                    type="radio"
                    name={element.name}
                    value="__other__"
                    checked={
                      showOther ||
                      (field.value &&
                        !choices.some((c) => c.value === field.value))
                    }
                    onChange={() => {
                      setShowOther(true);
                      field.onChange('');
                    }}
                    disabled={element.readOnly}
                  />
                  <span>{element.otherText || 'Outro'}</span>
                </label>
                {showOther && (
                  <input
                    type="text"
                    className={`${styles.input} ${styles.otherInput}`}
                    placeholder={
                      element.otherPlaceholder || 'Digite sua resposta'
                    }
                    value={field.value === '__other__' ? '' : field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    disabled={element.readOnly}
                  />
                )}
              </>
            )}
          </div>
        )}
      />
      {(error || validationError) && (
        <p className={styles.errorMessage}>
          {(error?.message as string) || validationError}
        </p>
      )}
    </div>
  );
};

export default RadioGroupField;
